import React, { useEffect } from 'react'
import { openInExternalBrowser, useCopyToClipboard } from 'utils'

/**
 * Full-screen warning modal for in-app browser detection.
 * Used by dynamic redirect components (TeacherPublicPage, BookingFlow, ConsultationFormModal)
 * where the Google login is triggered programmatically, not via a visible button.
 */
export default function InAppBrowserWarningModal({ isOpen, onClose, translations }) {
  const { copyToClipboard, isCopied } = useCopyToClipboard()
  const raw = translations || {}
  // Normalize key names: teaching_teams.in_app_browser uses modal_title/open_browser/etc,
  // seo.login_modal uses in_app_warning_title/in_app_open_browser/etc
  const t = {
    in_app_warning_title: raw.in_app_warning_title || raw.modal_title,
    in_app_warning_reason: raw.in_app_warning_reason || raw.modal_reason,
    in_app_warning_hint: raw.in_app_warning_hint || raw.paste_hint,
    in_app_open_browser: raw.in_app_open_browser || raw.open_browser,
    in_app_copy_url: raw.in_app_copy_url || raw.copy_url,
    in_app_copied_hint: raw.in_app_copied_hint || raw.copied_hint,
  }

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose()
    }
    // Restore whatever was there, not a literal. See LoginModal for why.
    const previousOverflow = document.body.style.overflow
    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-md lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col">
        {/* Mobile header */}
        <div className="flex items-center px-4 py-3 border-b border-gray-200 lg:hidden">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors cursor-pointer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>

        {/* Desktop header */}
        <div className="hidden lg:flex px-6 py-4 border-b items-center justify-between" style={{ backgroundColor: '#E6F0FF' }}>
          <h2 className="text-lg font-bold" style={{ color: '#3366FF' }}>
            {t.in_app_warning_title || 'Cannot use Google login in this browser'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center text-center">
          <div className="text-5xl mb-4">🔒</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2 lg:hidden">
            {t.in_app_warning_title || 'Cannot use Google login in this browser'}
          </h3>
          <p className="text-sm text-gray-600 mb-2">
            {t.in_app_warning_reason || 'Google does not allow sign-in from in-app browsers'}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {t.in_app_warning_hint || 'Copy the URL and open in Safari or Chrome'}
          </p>

          <button
            onClick={async () => {
              const result = await openInExternalBrowser()
              if (result === null) copyToClipboard(window.location.href)
            }}
            className="w-full max-w-xs py-3 rounded-lg font-medium text-white text-sm transition-colors cursor-pointer"
            style={{ backgroundColor: isCopied ? '#10B981' : '#3366FF' }}
          >
            {isCopied
              ? (t.in_app_copied_hint || 'Copied! Paste in Safari or Chrome')
              : (t.in_app_open_browser || 'Open in Browser')}
          </button>
          {!isCopied && (
            <button
              onClick={() => copyToClipboard(window.location.href)}
              className="w-full max-w-xs mt-2 py-2 text-sm text-amber-700 hover:text-amber-900 transition-colors cursor-pointer"
            >
              {t.in_app_copy_url || 'Copy URL'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
